import { useCallback } from "react";

import { Trans } from "@lingui/react/macro";
import { useSession } from "@pontimed/auth-client";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";

import {
  NOTIFICATIONS_VALID_TABS,
  NotificationsView,
  type NotificationsTab,
} from "@/components/notifications/notifications-view";

interface NotificationsSearch {
  tab: NotificationsTab;
}

export const Route = createFileRoute("/_authenticated/account/admin/notifications")({
  validateSearch: (search: Record<string, unknown>): NotificationsSearch => {
    const raw = typeof search.tab === "string" ? search.tab : "";
    return {
      tab: NOTIFICATIONS_VALID_TABS.includes(raw as NotificationsTab)
        ? (raw as NotificationsTab)
        : "all",
    };
  },
  beforeLoad: () => {
    // Hydrate-time gate: API also enforces 403 on workspaceType=admin requests
    // for non-superadmin tokens, but bouncing here avoids loading the page chrome.
    if (typeof window !== "undefined") {
      const cached = window.localStorage.getItem("pontimed.session.cache");
      if (cached) {
        try {
          const parsed = JSON.parse(cached) as { user?: { isSuperadmin?: boolean } };
          if (parsed?.user?.isSuperadmin !== true) {
            throw redirect({ to: "/account/user/notifications", search: { tab: "all" } });
          }
        } catch {
          /* fall through — runtime gate below catches it */
        }
      }
    }
  },
  component: AdminNotificationsPage,
});

function AdminNotificationsPage() {
  const { tab: activeTab } = Route.useSearch();
  const navigate = useNavigate();
  const { user } = useSession();

  const setActiveTab = useCallback(
    (key: NotificationsTab) =>
      navigate({ to: "/account/admin/notifications", search: { tab: key } }),
    [navigate],
  );

  if (user && user.isSuperadmin !== true) {
    void navigate({ to: "/account/user/notifications", search: { tab: "all" } });
    return null;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <header>
        <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
          <Trans>Admin notifications</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>Platform-wide alerts surfaced to superadmin accounts.</Trans>
        </p>
      </header>
      <NotificationsView workspaceType="admin" activeTab={activeTab} onTabChange={setActiveTab} />
    </div>
  );
}
